/**
 * AuthErrorBanner — shared error banner for auth screens.
 * Coral-tinted background with AlertCircle icon and error message.
 */
import { View, Text } from 'react-native'
import Animated from 'react-native-reanimated'
import { AlertCircle } from '@/components/ui/icons'
import { COLORS, FONTS, SPACING, RADIUS } from '../../lib/brand'

import { useEntrance } from '@/lib/hooks/use-entrance'

interface AuthErrorBannerProps {
  error: string | null
  /** Place banner below content (marginTop) instead of default marginBottom */
  marginTop?: boolean
}

export function AuthErrorBanner({ error, marginTop }: AuthErrorBannerProps) {
  const enter = useEntrance()
  if (!error) return null

  return (
    <Animated.View
      collapsable={false}
      entering={enter()}
      accessibilityRole="alert"
      accessibilityLiveRegion="assertive"
      style={{
        backgroundColor: COLORS.coral.dark,
        borderRadius: RADIUS.md,
        // Was `SPACING.md - 2` / `SPACING.sm`: a raw offset, and 34pt tall —
        // shorter than the 44pt controls it interrupts, so the one element on
        // the screen that has to be noticed was the smallest thing on it.
        paddingHorizontal: SPACING.md,
        paddingVertical: SPACING.md - SPACING.xs,
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
        ...(marginTop ? { marginTop: SPACING.md } : { marginBottom: SPACING.md }),
      }}
    >
      {/* Without flexShrink:0 the glyph squashes on web when the copy is long. */}
      <View style={{ flexShrink: 0 }}>
        <AlertCircle size={18} color={COLORS.white} />
      </View>
      <Text
        style={{
          color: COLORS.white,
          fontFamily: FONTS.body,
          fontSize: 13,
          lineHeight: 18,
          flex: 1,
        }}
      >
        {error}
      </Text>
    </Animated.View>
  )
}
